import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { UserPlus, Clock, CheckCircle, XCircle, Users } from 'lucide-react';
import { gameManagementService } from '@/services/gameManagementService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PlayerJoinRequestButtonProps {
  gameId: string;
  isAlreadyRequested: boolean;
  isGameLocked: boolean;
  gameStatus?: string;
  onRequestSuccess?: () => void;
  className?: string;
  homeTeamName?: string;
  awayTeamName?: string;
}

export const PlayerJoinRequestButton = ({ 
  gameId, 
  isAlreadyRequested, 
  isGameLocked,
  gameStatus,
  onRequestSuccess,
  className,
  homeTeamName,
  awayTeamName
}: PlayerJoinRequestButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showTeamSelection, setShowTeamSelection] = useState(false);
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  
  const currentUser = supabaseUser || internalUser;
  const userId = currentUser?.id;
  
  // Get user role to check eligibility
  const userRole = currentUser ? 
    (supabaseUser && (supabaseUser as any).profile?.role) || 
    (internalUser && internalUser.role) : null;

  // Check if user is eligible to join games
  const canJoinGame = () => {
    // Don't show button for cancelled games
    if (gameStatus === 'cancelled') return false;
    
    // Support roles cannot join as players
    if (userRole === 'support') return false;
    
    // Other eligibility checks
    return !isGameLocked && !isAlreadyRequested;
  };

  const handleRequestToJoin = async (teamSide: 'home' | 'away') => {
    if (!userId) {
      toast.error('You must be logged in to request to join a game');
      return;
    }

    // Additional role check at action time
    if (userRole === 'support') {
      toast.error('Support staff cannot join games as players. Use the scorekeeper application instead.');
      return;
    }

    setIsLoading(true);
    try {
      // First check if user already has a request for this game
      const { data: existingRequest, error: checkError } = await supabase
        .from('join_requests')
        .select('id')
        .eq('game_id', gameId)
        .eq('player_id', userId)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing request:', checkError);
        toast.error('Failed to check existing requests. Please try again.');
        return;
      }

      if (existingRequest) {
        toast.info('You have already requested to join this game');
        return;
      }

      // Create join request
      const { error: insertError } = await supabase
        .from('join_requests')
        .insert({
          game_id: gameId,
          player_id: userId,
          player_type: supabaseUser ? 'supabase' : 'internal',
          requested_team_side: teamSide
        });

      if (insertError) {
        console.error('Error creating join request:', insertError);
        toast.error('Failed to submit join request. Please try again.');
        return;
      }

      const teamName = teamSide === 'home' ? homeTeamName : awayTeamName;
      toast.success(`Join request submitted for ${teamName || `${teamSide} team`}! Waiting for approval.`);
      setShowTeamSelection(false);
      onRequestSuccess?.();
    } catch (error) {
      console.error('Error requesting to join game:', error);
      const errorMessage = error?.message || 'Failed to submit join request. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonText = () => {
    if (isLoading) return 'Submitting...';
    if (isAlreadyRequested) return 'Request Sent';
    if (gameStatus === 'cancelled') return 'Game Cancelled';
    if (userRole === 'support') return 'Not Available for Support Roles';
    if (isGameLocked) return 'Game Locked';
    return 'Request to Join';
  };

  const getButtonIcon = () => {
    if (isAlreadyRequested) return <CheckCircle className="h-4 w-4" />;
    if (gameStatus === 'cancelled' || userRole === 'support') return <XCircle className="h-4 w-4" />;
    if (isGameLocked) return <XCircle className="h-4 w-4" />;
    return <UserPlus className="h-4 w-4" />;
  };

  // Don't render button if user cannot join
  if (!canJoinGame()) {
    return null;
  }

  if (isAlreadyRequested) {
    return (
      <Button
        disabled
        size="sm"
        variant="secondary"
        className="bg-gray-200 text-gray-600 cursor-not-allowed"
      >
        <CheckCircle className="h-3 w-3 mr-1" />
        Request Sent
      </Button>
    );
  }

  return (
    <Dialog open={showTeamSelection} onOpenChange={setShowTeamSelection}>
      <DialogTrigger asChild>
        <Button
          disabled={isLoading || isAlreadyRequested || isGameLocked || gameStatus === 'cancelled' || userRole === 'support'}
          size="sm"
          variant={isGameLocked || gameStatus === 'cancelled' || userRole === 'support' ? "outline" : "default"}
          className={className}
        >
          {getButtonIcon()}
          {getButtonText()}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Team to Join</DialogTitle>
          <DialogDescription>
            Choose which team you would like to request to join. Your request will be sent to the team coach/owner for approval.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Button
            onClick={() => handleRequestToJoin('home')}
            disabled={isLoading}
            className="flex items-center gap-2"
            variant="outline"
          >
            <Users className="h-4 w-4" />
            Join {homeTeamName || 'Home Team'}
          </Button>
          <Button
            onClick={() => handleRequestToJoin('away')}
            disabled={isLoading}
            className="flex items-center gap-2"
            variant="outline"
          >
            <Users className="h-4 w-4" />
            Join {awayTeamName || 'Away Team'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};